import { useSelector } from 'react-redux';
import { Surface, Txt } from '@vst/beam';
import { useTranslation } from 'react-i18next';

import { useScreenResolution } from '@app/hooks';
import { selectChangePlan, selectFiberDowngradeFee, selectHasFiber } from '@app/redux';

import { GetPlanOffersPayload } from '@mfe/shared/schema-types';

import OneTimeFees from '..//OneTimeFees';
import MonthlyCharges from '../MonthlyCharges';
import PrimaryAttribute from '../PrimaryAttribute';
import { Characteristics } from './Characteristics';

import styles from './styles.module.scss';

type CartSummaryProps = {
  selectedPlan: GetPlanOffersPayload;
  handleBackNavigation: (e: React.MouseEvent<HTMLElement>) => void;
  requiresEquipmentChange: boolean;
};

const CartSummary = ({ selectedPlan, handleBackNavigation, requiresEquipmentChange }: CartSummaryProps) => {
  const { t } = useTranslation('ChangePlan', {
    keyPrefix: 'orderReview.cartDetails',
  });

  const { isExtraSmall, isSmall } = useScreenResolution();
  const isMobile = isSmall || isExtraSmall;

  const {
    cart: {
      prices: { oneTime },
    },
  } = useSelector(selectChangePlan);

  const isFiber = useSelector(selectHasFiber);
  const fiberDowngradeFee = useSelector(selectFiberDowngradeFee);
  const showOneTimeFees = requiresEquipmentChange || oneTime.feesAndDiscounts || (isFiber && fiberDowngradeFee);

  return (
    <Surface
      p="24px"
      variant="primary"
      data-cy="cart-summary-card"
      className={styles['cart-summary']}
      radius={isMobile ? undefined : '16px'}
    >
      <PrimaryAttribute />
      <Characteristics />
      <hr />
      <Txt variant="heading6" data-cy="new-monthly-charges">
        {t('pricingDetails.internetTotal')}
      </Txt>
      <MonthlyCharges handleBackNavigation={handleBackNavigation} />
      <Txt variant="smallRegular" color="subtle">
        {t('nextBillInfo')}
      </Txt>
      {showOneTimeFees && <OneTimeFees selectedPlan={selectedPlan} />}
      <Txt variant="tinyRegular" color="subtle">
        {t('pricingInfo')}
      </Txt>
    </Surface>
  );
};

export default CartSummary;
